import type { RuleResult } from './RulesEngine';

export type NotificationChannel = 'desktop' | 'console';

export interface Notification {
  id: string;
  title: string;
  body: string;
  severity: RuleResult['severity'];
  timestamp: Date;
  read: boolean;
}

export class NotificationService {
  private notifications: Notification[] = [];
  private listeners: Set<(notifications: Notification[]) => void> = new Set();
  private desktopGranted = false;
  private maxNotifications = 100;

  constructor() {
    if (typeof window !== 'undefined') {
      this.checkDesktopPermission();
    }
  }

  async checkDesktopPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      this.desktopGranted = false;
      return false;
    }

    if (Notification.permission === 'granted') {
      this.desktopGranted = true;
      return true;
    }

    if (Notification.permission === 'default') {
      try {
        const permission = await Notification.requestPermission();
        this.desktopGranted = permission === 'granted';
        return this.desktopGranted;
      } catch {
        this.desktopGranted = false;
        return false;
      }
    }

    this.desktopGranted = false;
    return false;
  }

  notify(ruleResult: RuleResult): void {
    if (!ruleResult.triggered) return;

    const notification: Notification = {
      id: `${ruleResult.ruleId}-${Date.now()}`,
      title: `[${ruleResult.severity.toUpperCase()}] ${ruleResult.ruleName}`,
      body: ruleResult.message,
      severity: ruleResult.severity,
      timestamp: new Date(),
      read: false,
    };

    this.notifications.push(notification);
    if (this.notifications.length > this.maxNotifications) {
      this.notifications.shift();
    }

    this.notifyListeners();

    if (ruleResult.severity === 'critical') {
      this.sendDesktop(notification);
    }
  }

  private sendDesktop(notification: Notification): void {
    if (!this.desktopGranted) return;

    try {
      new Notification(notification.title, {
        body: notification.body,
        icon: '/favicon.ico',
        tag: notification.id,
      });
    } catch {
      // Notificação desktop falhou
    }
  }

  markAsRead(id: string): void {
    const notif = this.notifications.find((n) => n.id === id);
    if (notif) {
      notif.read = true;
      this.notifyListeners();
    }
  }

  markAllAsRead(): void {
    this.notifications.forEach((n) => (n.read = true));
    this.notifyListeners();
  }

  clear(): void {
    this.notifications = [];
    this.notifyListeners();
  }

  getNotifications(): Notification[] {
    return [...this.notifications];
  }

  getUnreadCount(): number {
    return this.notifications.filter((n) => !n.read).length;
  }

  subscribe(callback: (notifications: Notification[]) => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  private notifyListeners(): void {
    const copy = [...this.notifications];
    this.listeners.forEach((cb) => cb(copy));
  }
}
